import { useState, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../redux/store";
import { Alert } from "@mui/material";

import {
  fetchCustomers,
  selectCustomer,
  handleClearStatus,
} from "../../../redux/slices/customersSlice";

import CustomerDrawer from "./components/CustomersDrawer";
import BillingAddressesDialog from "./components/BillingAddressesDialog";
import CustomerUsersCell from "./components/CustomerUsersCell";
import Table from "../../../components/Table";
import ProviderBadge from "../../../components/ProviderBadge";
import CustomersTableControls from "./components/TableControls";

import { fetchUsers } from "../users/usersSlice";

const Customers = () => {
  const dispatch = useAppDispatch();
  const { customers, status, error, filters } = useAppSelector(
    (state) => state.customers,
  );

  const [billingDialogOpen, setBillingDialogOpen] = useState(false);

  const source = useAppSelector((state) => state.source.source);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchUsers());
    }
  }, [status, dispatch]);

  // The list belongs to one accounting system at a time, so it follows the
  // switch rather than being loaded once.
  useEffect(() => {
    dispatch(fetchCustomers());
  }, [source, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(handleClearStatus());
    };
  }, [dispatch]);

  return (
    <>
      <CustomersTableControls
        onBillingAddressesClick={
          source === "ies" ? undefined : () => setBillingDialogOpen(true)
        }
      />
      {status === "failed" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || "Failed to load customers. Please try again."}
        </Alert>
      )}
      <Table
        enableColumnSearch
        headers={[
          { key: "name", displayName: "Name" },
          { key: "email", displayName: "Email" },
          { key: "status", displayName: "Status", disableColumnSearch: true },
          { key: "origin", displayName: "Origin" },
          {
            key: "providerId",
            displayName: "Source",
            disableColumnSearch: true,
            format: ({ row }: any) => (
              <ProviderBadge source={row?.source} providerId={row?.providerId} />
            ),
          },
          // Who can log in, which is what says whether a customer has anyone
          // who can actually pay: the client's own people, then the staff who
          // look after them.
          {
            key: "clientUsers",
            displayName: "Client users",
            disableColumnSearch: true,
            format: ({ value }: any) => <CustomerUsersCell users={value} />,
          },
          {
            key: "employeeUsers",
            displayName: "TPC employees",
            disableColumnSearch: true,
            format: ({ value }: any) => <CustomerUsersCell users={value} />,
          },
        ]}
        rows={customers}
        actions={{
          edit: (id) => dispatch(selectCustomer(id)),
        }}
        filters={filters}
        loading={status === "loading"}
      />
      <CustomerDrawer />
      <BillingAddressesDialog
        open={billingDialogOpen}
        onClose={() => setBillingDialogOpen(false)}
      />
    </>
  );
};

export default Customers;
